import { PaletteColor, alpha } from '@mui/material';
import colors from './colors.module.scss';
import { Themes } from './themeTypes';

const withAlphas = (color: PaletteColor) => {
  if (!color || !color.main) {
    console.error('Invalid color object:', color);
    return color; // Return the color as-is to avoid errors
  }

  return {
    ...color,
    alpha4: alpha(color.main, 0.04),
    alpha8: alpha(color.main, 0.08),
    alpha12: alpha(color.main, 0.12),
    alpha30: alpha(color.main, 0.30),
    alpha50: alpha(color.main, 0.50),
  };
};

export const getThemeNames = (theme: Themes): string => {
  switch (Number(theme)) {
    case Themes.SYSTEM:
      return 'system';
    case Themes.DARK:
      return 'dark';
    case Themes.LIGHT:
    default:
      return 'light';
  }
};

export const renderThemes = (theme?: Themes) => {
  switch (Number(theme)) {
    case Themes.DARK:
      return {
        primary: colors.mpg_dark_palette_primary,
        text_color: colors.mpg_dark_palette_text_color,
        sidenav_color: colors.mpg_dark_palette_side_nav_color,
        secondary: colors.mpg_dark_palette_secondary,
        contrastText: colors.mpg_dark_palette_contained_text,
        default_background: colors.mpg_dark_palette_default_background,
        paper_background: colors.mpg_dark_palette_paper_background,
        primary_component_border: colors.mpg_dark_palette_primary_component_border,
        divider: colors.mpg_dark_palette_divider,
        dialog_background: colors.mpg_dark_palette_dialog_background,
        dialog_title: colors.mpg_dark_palette_dialog_title,
        dialog_text: colors.mpg_dark_palette_dialog_text,
        tooltip_color: colors.mpg_dark_palette_tooltip_color,
        data_table_header: colors.mpg_dark_palette_data_table_header,
        data_header_text: colors.mpg_dark_palette_data_header_text,
        data_table_body: colors.mpg_dark_palette_data_table_body,
        data_table_rollover: colors.mpg_dark_palette_data_table_rollover,
        data_table_text: colors.mpg_dark_palette_data_table_text,
        calendar_current_day: colors.mpg_dark_palette_calendar_current_day,
        calendar_weekend: colors.mpg_dark_palette_calendar_weekend,
        calendar_main: colors.mpg_dark_palette_calendar_main,
        top_nav_text_color: colors.mpg_dark_palette_top_nav_text_color,
        calendar_line: colors.mpg_dark_palette_calendar_line,
        calendar_week_heading: colors.mpg_dark_palette_calendar_week_heading,
        calendar_text: colors.mpg_dark_palette_calendar_text,
        calendar_text_hover: colors.mpg_dark_palette_calendar_text_hover,
        accordion_color: colors.mpg_dark_palette_accordion_color,
        secondary_component_hover: colors.mpg_dark_palette_secondary_component_hover,
        impersonate_color: colors.mpg_dark_palette_impersonate_color,
        disabled_color: colors.mpg_dark_palette_disabled_color,
        checkpoint_question: colors.mpg_dark_checkpoint_question,
        contained_text: colors.mpg_dark_palette_contained_text,

        primary_component: withAlphas({
          light: colors.mpg_dark_palette_purple_light,
          main: colors.mpg_dark_palette_primary_component,
          dark: colors.mpg_dark_palette_primary_component_hover,
          contrastText: colors.mpg_dark_palette_contained_text,
        }),
        success: withAlphas({
          light: colors.mpg_dark_palette_success_light,
          main: colors.mpg_dark_palette_success_main,
          dark: colors.mpg_dark_palette_success_dark,
          contrastText: colors.mpg_dark_palette_contained_text,
        }),
        info: withAlphas({
          light: colors.mpg_dark_palette_info_light,
          main: colors.mpg_dark_palette_info_main,
          dark: colors.mpg_dark_palette_info_dark,
          contrastText: colors.mpg_dark_palette_contained_text,
        }),
        warning: withAlphas({
          light: colors.mpg_dark_palette_warning_light,
          main: colors.mpg_dark_palette_warning_main,
          dark: colors.mpg_dark_palette_warning_dark,
          contrastText: colors.mpg_dark_palette_contained_text,
        }),
        error: withAlphas({
          light: colors.mpg_dark_palette_error_light,
          main: colors.mpg_dark_palette_error_main,
          dark: colors.mpg_dark_palette_error_dark,
          contrastText: colors.mpg_dark_palette_contained_text,
        }),
      };

    case Themes.LIGHT:
    default:
      return {
        primary: colors.mpg_light_palette_primary,
        text_color: colors.mpg_light_palette_text_color,
        secondary: colors.mpg_light_palette_secondary,
        sidenav_color: colors.mpg_light_palette_side_nav_color,
        contrastText: colors.mpg_light_palette_contained_text,
        default_background: colors.mpg_light_palette_default_background,
        paper_background: colors.mpg_light_palette_paper_background,
        primary_component_border: colors.mpg_light_palette_primary_component_border,
        dialog_background: colors.mpg_light_palette_dialog_background,
        dialog_title: colors.mpg_light_palette_dialog_title,
        dialog_text: colors.mpg_light_palette_dialog_text,
        tooltip_color: colors.mpg_light_palette_tooltip_color,
        divider: colors.mpg_light_palette_divider,
        data_table_header: colors.mpg_light_palette_data_table_header,
        data_header_text: colors.mpg_light_palette_data_header_text,
        data_table_body: colors.mpg_light_palette_data_table_body,
        data_table_rollover: colors.mpg_light_palette_data_table_rollover,
        data_table_text: colors.mpg_light_palette_data_table_text,
        calendar_current_day: colors.mpg_light_palette_calendar_current_day,
        calendar_weekend: colors.mpg_light_palette_calendar_weekend,
        calendar_main: colors.mpg_light_palette_calendar_main,
        top_nav_text_color: colors.mpg_light_palette_top_nav_text_color,
        calendar_line: colors.mpg_light_palette_calendar_line,
        calendar_week_heading: colors.mpg_light_palette_calendar_week_heading,
        calendar_text: colors.mpg_light_palette_calendar_text,
        calendar_text_hover: colors.mpg_light_palette_calendar_text_hover,
        accordion_color: colors.mpg_light_palette_accordion_color,
        secondary_component_hover: colors.mpg_light_palette_secondary_component_hover,
        impersonate_color: colors.mpg_light_palette_impersonate_color,
        disabled_color: colors.mpg_light_palette_disabled_color,
        checkpoint_question: colors.mpg_light_checkpoint_question,
        contained_text: colors.mpg_light_palette_contained_text,

        primary_component: withAlphas({
          light: colors.mpg_light_palette_purple_light,
          main: colors.mpg_light_palette_primary_component,
          dark: colors.mpg_light_palette_primary_component_hover,
          contrastText: colors.mpg_light_palette_contained_text,
        }),
        success: withAlphas({
          light: colors.mpg_light_palette_success_light,
          main: colors.mpg_light_palette_success_main,
          dark: colors.mpg_light_palette_success_dark,
          contrastText: colors.mpg_light_palette_contained_text,
        }),
        info: withAlphas({
          light: colors.mpg_light_palette_info_light,
          main: colors.mpg_light_palette_info_main,
          dark: colors.mpg_light_palette_info_dark,
          contrastText: colors.mpg_light_palette_contained_text,
        }),
        warning: withAlphas({
          light: colors.mpg_light_palette_warning_light,
          main: colors.mpg_light_palette_warning_main,
          dark: colors.mpg_light_palette_warning_dark,
          contrastText: colors.mpg_light_palette_contained_text,
        }),
        error: withAlphas({
          light: colors.mpg_light_palette_error_light,
          main: colors.mpg_light_palette_error_main,
          dark: colors.mpg_light_palette_error_dark,
          contrastText: colors.mpg_light_palette_contained_text,
        }),

      };
  }
};

export { Themes };
